import { ReactComponent as ChatButtonIcon } from '@assets/components/panel/chat/chat-button.svg';
import { Box, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import CockpitCustomScrollbar from '@components/common/layout/CockpitCustomScrollbar';
import { KeyboardEvent, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

type Props = {
  onSubmit: (message: string) => void;
};

const MAX_LENGTH = 500;

export function MessageInput({ onSubmit }: Readonly<Props>) {
  const [isDisabled, setIsDisabled] = useState(true);
  const [size, setSize] = useState<number>(0);
  const inputRef = useRef<HTMLDivElement>(null);

  const updateButtonState = () => {
    const input = inputRef.current;
    if (!input) return;

    const text = input.innerText;
    const isEmpty = text.trim() === '';
    const tooLong = text.length > MAX_LENGTH;

    setIsDisabled(isEmpty || tooLong);
    setSize(text.length);
  };

  const handleOnClick = () => {
    const input = inputRef.current;
    if (!input) return;

    onSubmit(input.innerText);
    input.innerText = '';
    updateButtonState();
  };

  const handleOnKeyDown = (event: KeyboardEvent) => {
    if (event.ctrlKey && !event.altKey && event.key === 'Enter' && !isDisabled) {
      handleOnClick();
    }
  };

  return (
    <VStack padding={3} gap={0.5} backgroundColor="neutral.800-op70" alignItems="start">
      <HStack border="1px solid" justifyContent="space-between" borderColor="neutral.300" height="106px" width="100%">
        <Box
          flexGrow={1}
          height="100%"
          padding={2}
          paddingRight={0}
          cursor="text"
          onClick={() => inputRef.current?.focus()}
        >
          <CockpitCustomScrollbar marginTop={0}>
            <Box
              ref={inputRef}
              fontFamily="roboto"
              whiteSpace="pre-wrap"
              contentEditable={navigator.userAgent.indexOf('Firefox') != -1 ? true : 'plaintext-only'}
              resize="none"
              lineHeight="24px"
              color="neutral.white"
              style={{ caretColor: 'var(--chakra-colors-sky-blue)' }}
              _focus={{ outline: 'none' }}
              onInput={updateButtonState}
              onKeyDown={handleOnKeyDown}
            />
          </CockpitCustomScrollbar>
        </Box>
        <IconButton
          height="100%"
          width="44px"
          disabled={isDisabled}
          borderRadius={0}
          backgroundColor="transparent"
          aria-label="send message"
          padding={0.25}
          color="skyBlue"
          border="none !important"
          _disabled={{ color: 'neutral.300', cursor: 'default', opacity: 0.5 }}
          onClick={handleOnClick}
        >
          <ChatButtonIcon style={{ height: '100%', width: '100%' }} />
        </IconButton>
      </HStack>
      <HStack gap={0} marginLeft={2}>
        <Text
          fontWeight="medium"
          lineHeight="15px"
          padding={0.25}
          color={size <= MAX_LENGTH ? 'neutral.300' : 'neutral.white'}
          backgroundColor={size <= MAX_LENGTH ? 'transparent' : 'red'}
        >
          {size}
        </Text>
        <Text fontWeight="medium" lineHeight="15px" color="neutral.300">
          <FormattedMessage id="components.chat.characters" values={{ maxLength: MAX_LENGTH }} />
        </Text>
      </HStack>
    </VStack>
  );
}
